import React, { useState, useEffect } from 'react'
import { Modal, Button, message, Spin, Typography, Space, Alert } from 'antd'
import { DownloadOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons'
import axios from 'axios'

const { Text, Title } = Typography

interface SchemaViewerProps {
  datasetId: string
  datasetName: string
  visible: boolean
  onClose: () => void
  onEdit: (schema: string) => void
}

const SchemaViewer: React.FC<SchemaViewerProps> = ({ 
  datasetId, 
  datasetName,
  visible, 
  onClose, 
  onEdit 
}) => {
  const [schema, setSchema] = useState('')
  const [loading, setLoading] = useState(false)
  const [version, setVersion] = useState(1)
  const [showDefaultSchema, setShowDefaultSchema] = useState(false)
  const [isGenerated, setIsGenerated] = useState(false)

  useEffect(() => {
    if (visible && datasetId) {
      loadSchema()
    }
  }, [visible, datasetId])

  const loadSchema = async () => {
    setLoading(true)
    setShowDefaultSchema(false)
    setIsGenerated(false)
    
    try {
      // Try new schema management endpoint first
      let response
      try {
        response = await axios.get(`http://localhost:5000/api/datasets/${datasetId}/schema`)
        setSchema(response.data.schema)
        setVersion(response.data.version || 1)
      } catch (error: any) {
        // Fallback to old YAML endpoint
        try {
          response = await axios.get(`http://localhost:5000/api/schema/datasets/${datasetId}/yaml`)
          setSchema(response.data.content)
          setVersion(1)
        } catch (yamlError) {
          // Generate default schema from training examples
          try {
            response = await axios.get(`http://localhost:5000/api/datasets/${datasetId}/schema/generate`)
            setSchema(response.data.schema)
            setShowDefaultSchema(true)
            setIsGenerated(true)
            message.info('Showing auto-generated schema from training examples')
          } catch (genError) {
            throw new Error('Could not load or generate schema')
          }
        }
      }
    } catch (error: any) {
      message.error('Failed to load schema')
      setShowDefaultSchema(true)
      setSchema('# No schema available\n# Upload a custom schema to get started')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (!schema) {
      message.warning('No schema to download')
      return
    }
    const blob = new Blob([schema], { type: 'text/yaml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${datasetName.replace(/\s+/g, '_')}_schema.yaml`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    message.success('Schema downloaded!')
  }

  const handleEdit = () => {
    if (!schema || schema.includes('# No schema available')) {
      message.warning('Please upload a schema first')
      return
    }
    onEdit(schema)
    onClose()
  }

  const handleUpload = () => {
    window.location.href = '/training/datasets/upload'
  }

  return (
    <Modal
      title={
        <Space direction="vertical" size={0}>
          <Title level={4} style={{ margin: 0 }}>{datasetName} - Schema</Title>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {isGenerated ? 'Auto-generated' : `Version ${version}`}
          </Text>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        showDefaultSchema && (
          <Button key="upload" icon={<UploadOutlined />} onClick={handleUpload}>
            Upload Custom Schema
          </Button>
        ),
        <Button 
          key="download" 
          icon={<DownloadOutlined />} 
          onClick={handleDownload}
          disabled={!schema || schema.includes('# No schema available')}
        >
          Download YAML
        </Button>,
        <Button 
          key="edit" 
          type="primary" 
          icon={<EditOutlined />} 
          onClick={handleEdit}
          disabled={!schema || schema.includes('# No schema available')}
        >
          Edit Schema & Regenerate
        </Button>,
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>Loading schema...</Text>
          </div>
        </div>
      ) : (
        <>
          {showDefaultSchema && (
            <Alert
              message={isGenerated ? "Auto-Generated Schema" : "No Custom Schema Uploaded"}
              description={
                isGenerated 
                  ? "This schema was automatically generated from your training examples. You can upload a custom schema to regenerate the dataset."
                  : "This dataset was auto-generated. Upload a custom YAML schema to regenerate with your specific structure."
              }
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
          
          <pre style={{
            background: '#f5f5f5',
            padding: '16px',
            borderRadius: '6px',
            fontSize: '13px',
            lineHeight: '1.5',
            maxHeight: '600px',
            overflow: 'auto',
            border: '1px solid #e8e8e8',
            fontFamily: 'Monaco, Consolas, "Courier New", monospace'
          }}>
            {schema || '# Loading...'}
          </pre>
        </>
      )}
    </Modal>
  )
}

export default SchemaViewer
